// Admin editor for the rotating top-of-page marquee strip.
// Reads/writes settings key `site_marquee_messages` (array of strings).
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "../../api";
import { DEFAULT_MARQUEE_MESSAGES } from "../../components/PageContent";

const cryptoId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
  `m-${Math.random().toString(36).slice(2, 10)}`;

export const MarqueeEditor = () => {
  const settingsKey = "site_marquee_messages";
  // each row carries a UUID so React keys stay stable across reorder/delete
  const [rows, setRows] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  /* eslint-disable */
  useEffect(() => {
    let active = true;
    api
      .get(`/settings/${encodeURIComponent(settingsKey)}`)
      .then((r) => {
        if (!active) return;
        const v = r.data?.value;
        const list = Array.isArray(v) && v.length ? v.map(String) : DEFAULT_MARQUEE_MESSAGES;
        const seeded = list.map((text) => ({ id: cryptoId(), text }));
        setRows(seeded);
        setSaved(seeded.map((r) => r.text));
      })
      .catch(() => {
        const seeded = DEFAULT_MARQUEE_MESSAGES.map((text) => ({ id: cryptoId(), text }));
        setRows(seeded);
        setSaved(seeded.map((r) => r.text));
      });
    return () => {
      active = false;
    };
  }, []);
  /* eslint-enable */

  const current = rows.map((r) => r.text.trim()).filter(Boolean);
  const dirty = JSON.stringify(current) !== JSON.stringify(saved);

  const save = async () => {
    setLoading(true);
    setStatus(null);
    try {
      await api.put(`/admin/settings/${encodeURIComponent(settingsKey)}`, { value: current });
      const verify = await api.get(`/settings/${encodeURIComponent(settingsKey)}`);
      const persisted = verify.data?.value;
      const ok = Array.isArray(persisted) && persisted.length === current.length;
      setSaved(current);
      setStatus({
        kind: "ok",
        message: ok
          ? `${current.length} message${current.length === 1 ? "" : "s"} saved & verified — refresh any page to see it`
          : "Save returned 200 but server still shows old data. Retry, or contact support.",
        at: new Date(),
      });
      toast.success("Marquee saved.");
    } catch (e) {
      const httpStatus = e?.response?.status;
      const detail = formatError(e?.response?.data?.detail);
      const message =
        httpStatus === 401 || httpStatus === 403
          ? "Session expired — log out, log back in, then save again."
          : detail
          ? `Save failed (HTTP ${httpStatus || "?"}): ${detail}`
          : `Save failed: ${e?.message || "Network error"}.`;
      setStatus({ kind: "err", message, at: new Date() });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => setRows([...rows, { id: cryptoId(), text: "" }]);
  const removeRow = (id) => setRows(rows.filter((r) => r.id !== id));
  const updateRow = (id, text) =>
    setRows(rows.map((r) => (r.id === id ? { ...r, text } : r)));
  const moveRow = (id, dir) => {
    const idx = rows.findIndex((r) => r.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next);
  };
  const resetToDefaults = () => {
    if (!window.confirm("Reset the marquee to the original Chibah copy?")) return;
    setRows(DEFAULT_MARQUEE_MESSAGES.map((text) => ({ id: cryptoId(), text })));
  };

  return (
    <div className="border-4 border-terra bg-bone shadow-hard p-6 space-y-5" data-testid="marquee-editor">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="font-display text-2xl uppercase tracking-tight">Top Marquee Strip</div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink">
          {current.length} message{current.length === 1 ? "" : "s"}
        </span>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink leading-relaxed">
        The rotating banner at the very top of every page. Add, edit, reorder or remove
        lines. Each row renders as &quot;✦ {`{your message}`}&quot;.
      </p>

      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={row.id} className="flex items-center gap-2 border-2 border-asphalt bg-card-light p-2" data-testid={`marquee-row-${i}`}>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] bg-terra text-bone px-2 py-1 shrink-0">#{i + 1}</span>
            <input
              value={row.text}
              onChange={(e) => updateRow(row.id, e.target.value)}
              placeholder="Established under suspicious circumstances"
              data-testid={`marquee-row-${i}-input`}
              className="flex-1 border-2 border-asphalt bg-bone px-3 py-2 font-body"
            />
            <div className="flex gap-1 shrink-0">
              <button type="button" onClick={() => moveRow(row.id, -1)} disabled={i === 0} className="border-2 border-asphalt px-2 py-1 font-mono text-[10px] disabled:opacity-30" data-testid={`marquee-row-${i}-up`}>↑</button>
              <button type="button" onClick={() => moveRow(row.id, 1)} disabled={i === rows.length - 1} className="border-2 border-asphalt px-2 py-1 font-mono text-[10px] disabled:opacity-30" data-testid={`marquee-row-${i}-down`}>↓</button>
              <button type="button" onClick={() => removeRow(row.id)} className="border-2 border-terra bg-terra text-bone px-2 py-1 font-mono text-[10px]" data-testid={`marquee-row-${i}-remove`}>×</button>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="border-2 border-dashed border-asphalt p-6 text-center font-mono text-xs uppercase tracking-[0.25em] text-ink">
            ✦ Marquee is empty. Add a line or click Reset to bring back the defaults.
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 pt-3 border-t-2 border-asphalt/30 items-start">
        <button
          type="button"
          onClick={addRow}
          data-testid="marquee-add"
          className="border-2 border-asphalt px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-asphalt hover:text-bone"
        >
          + Add line
        </button>
        <button
          type="button"
          disabled={loading || !dirty}
          onClick={save}
          data-testid="marquee-save"
          className="bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra disabled:opacity-50"
        >
          {loading ? "Saving…" : dirty ? "Save marquee" : "All saved"}
        </button>
        <button
          type="button"
          onClick={resetToDefaults}
          data-testid="marquee-reset"
          className="bg-bone text-asphalt border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-asphalt hover:text-bone"
        >
          Reset to default
        </button>
        {status && (
          <div
            className={`flex-1 min-w-[220px] border-2 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] leading-snug ${
              status.kind === "ok"
                ? "border-asphalt bg-bone text-asphalt"
                : "border-terra bg-terra/10 text-terra"
            }`}
            data-testid="marquee-status"
          >
            <div className="text-[10px] opacity-70 mb-0.5">
              {status.at.toLocaleTimeString()} — {status.kind === "ok" ? "✓ saved" : "✗ failed"}
            </div>
            <div className="normal-case tracking-normal text-xs">{status.message}</div>
          </div>
        )}
      </div>
    </div>
  );
};
